//reportBuilder.ts
import { AccountEntry, CategoryTemplate } from '../types';
import { compareMonthYear, getPeriodDays } from './calculations';
import {
  getLineItemValue,
  getAvailableMonths,
  getAverageBalance
} from '../data/financialReportsHelper';

export function buildPLDetailReport(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  months: string[],
  periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'
) {
  // Convert periodType to viewType for internal calculations
  const viewType = periodType === 'annual' ? 'yearly' : periodType === 'quarterly' ? 'quarterly' : 'monthly';
  
  // Debug: Log P&L calculation inputs for annual data
  if (periodType === 'annual') {
    console.log('[P&L DEBUG] Building P&L Detail Report for ANNUAL data:', {
      periodType,
      viewType,
      totalAccounts: accounts.length,
      months: months,
      sampleRevenueAccounts: accounts.filter(acc => 
        activeTemplate.categories.find(cat => cat.value === acc.category)?.type === 'revenue'
      ).slice(0, 2).map(acc => ({
        accountNumber: acc.accountNumber,
        name: acc.name,
        month: acc.month,
        debit: acc.debit,
        credit: acc.credit,
        category: acc.category,
        netValue: acc.credit - acc.debit
      }))
    });
  }
  
  // Define P&L sections (customize as needed)
  const plSections = [
    { title: 'Revenue', categories: activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value), isRevenue: true, isContraRevenue: false },
    { title: 'Sales Discounts and Returns', categories: activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value), isRevenue: false, isContraRevenue: true },
    { title: 'Cost of Goods Sold', categories: activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value), isRevenue: false, isContraRevenue: false },
    { title: 'Operating Expenses', categories: activeTemplate.categories.filter(cat => cat.type === 'operating-expense').map(cat => cat.value), isRevenue: false, isContraRevenue: false },
    { title: 'Other Income (Expense)', categories: activeTemplate.categories.filter(cat => ['interest-expense', 'income-tax-expense', 'other-expense'].includes(cat.type)).map(cat => cat.value), isRevenue: false, isContraRevenue: false }
  ];

  const getAccountDisplayOrderPL = (categoryValue: string) => {
    const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))]
      .filter((acc): acc is string => typeof acc === 'string' && !!acc);
    return uniqueAccountNumbers.sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const sortedMonthsPL = [...months].sort(compareMonthYear);
  const plDetailData = {
    periods: sortedMonthsPL,
    line_items: [] as any[]
  };

  // Helper functions for calculated totals
  const calculateCategoryTotalPL = (
    accounts: AccountEntry[],
    category: string,
    month: string,
    viewType: string,
    isRevenue: boolean,
    isContraRevenue: boolean
  ): number => {
    const monthlyAccounts = accounts.filter(a => a.category === category && a.month === month);
    
    // Debug: Log calculation details for revenue categories in annual data
    if (periodType === 'annual' && isRevenue && monthlyAccounts.length > 0) {
      const total = monthlyAccounts.reduce((total, account) => {
        const accountValue = account.credit - account.debit;
        return total + accountValue;
      }, 0);
      
      console.log('[P&L DEBUG] Revenue calculation:', {
        category,
        month,
        viewType,
        accountsFound: monthlyAccounts.length,
        accounts: monthlyAccounts.map(acc => ({
          accountNumber: acc.accountNumber,
          name: acc.name,
          debit: acc.debit,
          credit: acc.credit,
          netValue: acc.credit - acc.debit
        })),
        categoryTotal: total
      });
    }
    
    return monthlyAccounts.reduce((total, account) => {
      if (isRevenue || isContraRevenue) {
        return total + (account.credit - account.debit);
      } else {
        return total + (account.debit - account.credit);
      }
    }, 0);
  };
  const calculateSectionTotalPL = (
    accounts: AccountEntry[],
    categories: string[],
    month: string,
    viewType: string,
    isRevenue: boolean,
    isContraRevenue: boolean
  ): number => {
    return categories.reduce((total, category) => total + calculateCategoryTotalPL(accounts, category, month, viewType, isRevenue, isContraRevenue), 0);
  };
  const calculateNetRevenueTotal = (month: string): number => {
    const revenue = calculateSectionTotalPL(accounts, plSections[0].categories, month, viewType, true, false);
    const contraRevenue = calculateSectionTotalPL(accounts, plSections[1].categories, month, viewType, false, true);
    return revenue + contraRevenue;
  };
  const calculateGrossMargin = (month: string): number => {
    const netRevenue = calculateNetRevenueTotal(month);
    const cogs = calculateSectionTotalPL(accounts, plSections[2].categories, month, viewType, false, false);
    return netRevenue - cogs;
  };
  const calculateOperatingIncome = (month: string): number => {
    const grossMargin = calculateGrossMargin(month);
    const operatingExpenses = calculateSectionTotalPL(accounts, plSections[3].categories, month, viewType, false, false);
    return grossMargin - operatingExpenses;
  };
  const calculatePLNetIncome = (month: string): number => {
    const operatingIncome = calculateOperatingIncome(month);
    const otherIncomeExpense = calculateSectionTotalPL(accounts, plSections[4].categories, month, viewType, false, false);
    return operatingIncome - otherIncomeExpense;
  };
  const calculateGrowthPercentage = (current: number, previous: number): number | null => {
    if (previous === 0) return null;
    return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
  };
  // Helper to get categories by EBITDA role
  const getCategoriesByEBITDARole = (role: string) =>
    activeTemplate.categories.filter(cat => cat.ebitdaRole === role).map(cat => cat.value);

  const calculateEBITDA = (
    accounts: AccountEntry[],
    month: string,
    viewType: string,
    netIncome: number
  ): number => {
    const interest = calculateSectionTotalPL(accounts, getCategoriesByEBITDARole('interest-expense'), month, viewType, false, false);
    const taxes = calculateSectionTotalPL(accounts, getCategoriesByEBITDARole('income-tax-expense'), month, viewType, false, false);
    const depreciation = calculateSectionTotalPL(accounts, getCategoriesByEBITDARole('depreciation'), month, viewType, false, false);
    const amortization = calculateSectionTotalPL(accounts, getCategoriesByEBITDARole('amortization'), month, viewType, false, false);
    return netIncome + interest + taxes + depreciation + amortization;
  };

  plSections.forEach(section => {
    if (section.categories.length === 0) return;
    plDetailData.line_items.push({
      type: 'section_header',
      name: section.title,
      level: 1
    });
    section.categories.forEach(categoryValue => {
      const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
      if (!category) return;
      const orderedAccountNumbers = getAccountDisplayOrderPL(String(categoryValue));
      if (orderedAccountNumbers.length === 0) return;
      plDetailData.line_items.push({
        type: 'category_header',
        name: category.label,
        category_value: String(categoryValue),
        nwc_role: category?.nwcRole || undefined,
        values: sortedMonthsPL.map(month => calculateCategoryTotalPL(accounts, String(categoryValue), month, viewType, section.isRevenue, section.isContraRevenue)),
        level: 2
      });
      orderedAccountNumbers.forEach((accountNumber: string) => {
        const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
        const monthlyValues = sortedMonthsPL.map(month => {
          const monthlyAccounts = accounts.filter(a => a.accountNumber === accountNumber && a.category === String(categoryValue) && a.month === month);
          const accountValue = monthlyAccounts.reduce((total, account) => {
            if (section.isRevenue) {
              return total + (account.credit - account.debit);
            } else if (section.isContraRevenue) {
              return total + (account.credit - account.debit);
            } else {
              return total + (account.debit - account.credit);
            }
          }, 0);
          return accountValue;
        });
        if (monthlyValues.some(val => val !== 0)) {
          plDetailData.line_items.push({
            type: 'account',
            name: accountName,
            account_number: accountNumber,
            category: String(categoryValue),
            values: monthlyValues,
            level: 3
          });
        }
      });
      const categoryTotalValues = sortedMonthsPL.map(month => {
        return calculateCategoryTotalPL(accounts, String(categoryValue), month, viewType, section.isRevenue, section.isContraRevenue);
      });
      if (categoryTotalValues.some(val => val !== 0)) {
        plDetailData.line_items.push({
          type: 'category_total',
          name: `Total ${category.label}`,
          category: String(categoryValue),
          values: categoryTotalValues,
          level: 2
        });
      }
    });
    const sectionTotalValues = sortedMonthsPL.map(month => {
      return calculateSectionTotalPL(accounts, section.categories, month, viewType, section.isRevenue, section.isContraRevenue);
    });
    if (sectionTotalValues.some(val => val !== 0)) {
      plDetailData.line_items.push({
        type: 'section_total',
        name: `Total ${section.title}`,
        values: sectionTotalValues,
        level: 1
      });
    }
    if (section.title === 'Sales Discounts and Returns') {
      const netRevenueValues = sortedMonthsPL.map(month => calculateNetRevenueTotal(month));
      plDetailData.line_items.push({
        type: 'calculated_total',
        name: 'Net Revenue',
        values: netRevenueValues,
        level: 1
      });
      const netRevenueGrowthValues = sortedMonthsPL.map((month, index) => {
        if (index === 0) return null;
        const currentRevenue = calculateNetRevenueTotal(month);
        const previousMonth = sortedMonthsPL[index - 1];
        const previousRevenue = calculateNetRevenueTotal(previousMonth);
        return calculateGrowthPercentage(currentRevenue, previousRevenue);
      });
      plDetailData.line_items.push({
        type: 'calculated_percentage',
        name: 'Net Revenue Growth %',
        values: netRevenueGrowthValues,
        level: 1
      });
    }
    if (section.title === 'Cost of Goods Sold') {
      const grossMarginValues = sortedMonthsPL.map(month => calculateGrossMargin(month));
      plDetailData.line_items.push({
        type: 'calculated_total',
        name: 'Gross Margin',
        values: grossMarginValues,
        level: 1
      });
      const grossMarginPercentValues = sortedMonthsPL.map(month => {
        const netRevenue = calculateNetRevenueTotal(month);
        const grossMargin = calculateGrossMargin(month);
        const marginPercent = netRevenue !== 0 ? (grossMargin / netRevenue) * 100 : 0;
        return Math.round(marginPercent * 10) / 10;
      });
      plDetailData.line_items.push({
        type: 'calculated_percentage',
        name: 'Gross Margin %',
        values: grossMarginPercentValues,
        level: 1
      });
    }
    if (section.title === 'Operating Expenses') {
      const operatingIncomeValues = sortedMonthsPL.map(month => calculateOperatingIncome(month));
      plDetailData.line_items.push({
        type: 'calculated_total',
        name: 'Operating Income',
        values: operatingIncomeValues,
        level: 1
      });
    }
  });
  const netIncomeValues = sortedMonthsPL.map(month => calculatePLNetIncome(month));
  plDetailData.line_items.push({
    type: 'calculated_total',
    name: 'Net Income',
    values: netIncomeValues,
    level: 0,
    is_final: true
  });
  const ebitdaValues = sortedMonthsPL.map((month, idx) => {
    const netIncome = netIncomeValues[idx];
    return calculateEBITDA(accounts, month, viewType, netIncome);
  });
  plDetailData.line_items.push({
    type: 'calculated_total',
    name: 'EBITDA',
    values: ebitdaValues,
    level: 0
  });
  const ebitdaMarginValues = sortedMonthsPL.map((month, idx) => {
    const netRevenue = calculateNetRevenueTotal(month);
    const ebitda = ebitdaValues[idx];
    if (netRevenue === 0) return null;
    return Math.round((ebitda / netRevenue) * 1000) / 10;
  });
  plDetailData.line_items.push({
    type: 'calculated_percentage',
    name: 'EBITDA Margin %',
    values: ebitdaMarginValues,
    level: 0
  });
  // Debug: Log final P&L report structure for annual data
  if (periodType === 'annual') {
    const revenueItems = plDetailData.line_items.filter(item => 
      item.name.toLowerCase().includes('revenue') || 
      item.name.toLowerCase().includes('sales')
    );
    
    console.log('[P&L DEBUG] Final P&L report structure for ANNUAL data:', {
      totalLineItems: plDetailData.line_items.length,
      periods: plDetailData.periods,
      revenueLineItems: revenueItems.map(item => ({
        type: item.type,
        name: item.name,
        values: item.values,
        category: item.category || 'N/A'
      }))
    });
  }

  return plDetailData;
}

export function buildBSDetailReport(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  months: string[]
) {
  // Define Balance Sheet sections
  const bsSections = [
    { id: 'current-assets', title: 'Current Assets', types: ['current-asset', 'contra-current-asset'] },
    { id: 'non-current-assets', title: 'Non-current Assets', types: ['non-current-asset', 'contra-non-current-asset'] },
    { id: 'current-liabilities', title: 'Current Liabilities', types: ['current-liability', 'contra-current-liability'] },
    { id: 'non-current-liabilities', title: 'Non-current Liabilities', types: ['non-current-liability', 'contra-non-current-liability'] },
    { id: 'equity', title: 'Equity', types: ['equity', 'contra-equity'] }
  ];

  const getCategoriesByType = (types: string[]) =>
    activeTemplate.categories
      .filter(cat => types.includes(cat.type))
      .map(cat => cat.value)
      .filter((v): v is string => typeof v === 'string' && !!v);
  const getCategoryDisplayName = (categoryValue: string) =>
    activeTemplate.categories.find(cat => cat.value === categoryValue)?.label || categoryValue;
  const getCategoryType = (category: string) =>
    activeTemplate.categories.find(cat => cat.value === category)?.type || 'n/a';
  const getCategoryAccountsByMonth = (category: string, month: string) =>
    accounts.filter(account => account.category === category && account.month === month);
  const calculateMonthlyTotal = (category: string, month: string) => {
    const monthlyAccounts = getCategoryAccountsByMonth(category, month);
    const categoryType = getCategoryType(category);
    const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
    return monthlyAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset)
        ? account.debit - account.credit
        : account.credit - account.debit);
    }, 0);
  };
  const calculateSectionTotal = (types: string[], month: string) => {
    const categories = getCategoriesByType(types);
    return categories.reduce((total, category) => total + calculateMonthlyTotal(category, month), 0);
  };
  const calculateTotalAssets = (month: string) =>
    calculateSectionTotal(['current-asset', 'contra-current-asset', 'non-current-asset', 'contra-non-current-asset'], month);
  const calculateTotalLiabilities = (month: string) =>
    calculateSectionTotal(['current-liability', 'contra-current-liability', 'non-current-liability', 'contra-non-current-liability'], month);
  const getMonthAccounts = (month: string) => accounts.filter(account => account.month === month);
  const calculateNetIncome = (month: string) => {
    const monthAccounts = getMonthAccounts(month);
    const revenueCategories = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value);
    const contraRevenueCategories = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value);
    const expenseCategories = activeTemplate.categories.filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 'income-tax-expense', 'other-expense'].includes(cat.type)).map(cat => cat.value);
    const revenue = monthAccounts.filter(account => revenueCategories.includes(account.category as string)).reduce((total, account) => total + (account.credit - account.debit), 0);
    const contraRevenue = monthAccounts.filter(account => contraRevenueCategories.includes(account.category as string)).reduce((total, account) => total + (account.debit - account.credit), 0);
    const expenses = monthAccounts.filter(account => expenseCategories.includes(account.category as string)).reduce((total, account) => total + (account.debit - account.credit), 0);
    return revenue - contraRevenue - expenses;
  };
  const calculateTotalEquity = (month: string) =>
    calculateSectionTotal(['equity', 'contra-equity'], month) + calculateNetIncome(month);
  const calculateLiabilitiesAndEquityTotal = (month: string) =>
    calculateTotalLiabilities(month) + calculateTotalEquity(month);
  const calculateCheck = (month: string) => {
    const totalAssets = calculateTotalAssets(month);
    const totalLiabilitiesAndEquity = calculateLiabilitiesAndEquityTotal(month);
    return totalAssets - totalLiabilitiesAndEquity;
  };
  const getAccountDisplayOrder = (categoryValue: string) => {
    const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))]
      .filter((acc): acc is string => typeof acc === 'string' && !!acc);
    return uniqueAccountNumbers.sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const sortedMonths = [...months].sort(compareMonthYear);
  const bsDetailData = {
    periods: sortedMonths,
    line_items: [] as any[]
  };

  bsSections.forEach((section, sectionIndex) => {
    const categories = getCategoriesByType(section.types);
    // Filter out undefined or falsy category values
    const filteredCategories = categories.filter((cv): cv is string => typeof cv === 'string' && !!cv);
    filteredCategories.forEach(categoryValue => {
      const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
      if (!category) return;
      // Check if this category has accounts in any month
      const hasAccounts = sortedMonths.some(month => getCategoryAccountsByMonth(categoryValue, month).length > 0);
      if (!hasAccounts) return;
      // Add category header
      bsDetailData.line_items.push({
        type: 'category_header',
        name: getCategoryDisplayName(categoryValue),
        category_value: categoryValue,
        nwc_role: category?.nwcRole || undefined,
        values: sortedMonths.map(month => calculateMonthlyTotal(categoryValue, month)),
        level: 2
      });
      // Get accounts in order
      const orderedAccountNumbers = getAccountDisplayOrder(categoryValue);
      // Process accounts
      orderedAccountNumbers.forEach((accountNumber: string) => {
        const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
        // Build monthly values array in sorted order
        const monthlyValues = sortedMonths.map(month => {
          const monthlyAccount = accounts.find(a => a.accountNumber === accountNumber && a.category === categoryValue && a.month === month);
          if (!monthlyAccount) return 0;
          const categoryType = getCategoryType(categoryValue);
          const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
          const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
          return (isAsset || isContraAsset)
            ? monthlyAccount.debit - monthlyAccount.credit
            : monthlyAccount.credit - monthlyAccount.debit;
        });
        // Only add accounts that have some non-zero data
        if (monthlyValues.some(val => val !== 0)) {
          bsDetailData.line_items.push({
            type: 'account',
            name: accountName,
            account_number: accountNumber,
            category: categoryValue,
            values: monthlyValues,
            level: 3
          });
        }
      });
      // Add category total
      const categoryTotalValues = sortedMonths.map(month => calculateMonthlyTotal(categoryValue, month));
      if (categoryTotalValues.some(val => val !== 0)) {
        bsDetailData.line_items.push({
          type: 'category_total',
          name: `Total ${getCategoryDisplayName(categoryValue)}`,
          category: categoryValue,
          values: categoryTotalValues,
          level: 2
        });
      }
    });
    // Add Current Period Net Income within Equity section
    if (section.id === 'equity') {
      const netIncomeValues = sortedMonths.map(month => calculateNetIncome(month));
      bsDetailData.line_items.push({
        type: 'calculated_total',
        name: 'Current Period Net Income',
        values: netIncomeValues,
        level: 2
      });
    }
    // Add section total
    const sectionTotalValues = sortedMonths.map(month => {
      return section.id === 'equity'
        ? calculateSectionTotal(section.types, month) + calculateNetIncome(month)
        : calculateSectionTotal(section.types, month);
    });
    if (sectionTotalValues.some(val => val !== 0)) {
      bsDetailData.line_items.push({
        type: 'section_total',
        name: `Total ${section.title}`,
        section_id: section.id,
        values: sectionTotalValues,
        level: 1
      });
    }
    // Add major totals after specific sections
    if (sectionIndex === 1) { // After Non-current Assets
      const totalAssetsValues = sortedMonths.map(month => calculateTotalAssets(month));
      bsDetailData.line_items.push({
        type: 'major_total',
        name: 'Total Assets',
        values: totalAssetsValues,
        level: 0
      });
    }
    if (sectionIndex === 3) { // After Non-current Liabilities
      const totalLiabilitiesValues = sortedMonths.map(month => calculateTotalLiabilities(month));
      bsDetailData.line_items.push({
        type: 'major_total',
        name: 'Total Liabilities',
        values: totalLiabilitiesValues,
        level: 0
      });
    }
  });
  // Add final totals
  const totalLiabilitiesAndEquityValues = sortedMonths.map(month => calculateLiabilitiesAndEquityTotal(month));
  bsDetailData.line_items.push({
    type: 'major_total',
    name: 'Total Liabilities and Equity',
    values: totalLiabilitiesAndEquityValues,
    level: 0
  });
  // Add balance check
  const checkValues = sortedMonths.map(month => calculateCheck(month));
  bsDetailData.line_items.push({
    type: 'balance_check',
    name: 'Check (Assets - L&E)',
    values: checkValues,
    level: 0,
    is_check: true
  });
  return bsDetailData;
}

export function buildCFDetailReport(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  months: string[]
) {
  // Utility to get all unique months from accounts, sorted
  const getAllMonths = (accounts: AccountEntry[]): string[] => {
    const months = [...new Set(accounts.map(a => a.month))];
    months.sort(compareMonthYear);
    return months;
  };
  // Helper to sum a category for a period
  const sumCategory = (category: string, period: string): number => {
    if (!category) return 0;
    if (!period) return 0;
    const categoryAccounts = accounts.filter(a => a.category === category && a.month === period);
    const categoryDef = activeTemplate.categories.find(cat => cat.value === category);
    if (categoryDef) {
      if (categoryDef.type.includes('liability') || categoryDef.type.includes('equity')) {
        return categoryAccounts.reduce((t, a) => t + (a.credit - a.debit), 0);
      }
    }
    return categoryAccounts.reduce((t, a) => t + (a.debit - a.credit), 0);
  };
  // Helper to get previous period
  const getPrevPeriod = (i: number, periods: string[]): string | null => (i === 0 ? null : periods[i - 1]);
  // Helper to get cash balance
  const getCashBalance = (cashCats: string[], period: string): number => {
    if (!period) return 0;
    return cashCats.reduce((sum, cat) => sum + sumCategory(cat || '', period), 0);
  };
  // Helper to get change in categories
  function getChange(cats: string[], period: string, prev: string | null, isCurrentAsset: boolean = false, isCurrentLiability: boolean = false): number | null {
    if (!prev) return null;
    const getBal = (p: string) => cats.reduce((sum, cat) => sum + sumCategory(cat, p), 0);
    const change = getBal(period) - getBal(prev);
    if (isCurrentAsset) return -change;
    if (isCurrentLiability) return change;
    return change;
  }
  // Helper to calculate sum change
  function calcSumChange(period: string, prevPeriod: string | null, categories: string[], adjustForDepreciation: boolean = false): number {
    if (!prevPeriod) return 0;
    const getBalance = (cat: string, p: string) => sumCategory(cat, p);
    let change = categories.reduce((sum, cat) => sum + (getBalance(cat, period) - getBalance(cat, prevPeriod)), 0);
    if (adjustForDepreciation) {
      const depreciationExpense = nonCashRows.filter(catObj => catObj.label.toLowerCase().includes('depreciation')).reduce((sum, catObj) => sum + sumCategory(catObj.value, period), 0);
      change += depreciationExpense;
    }
    return change;
  }
  // Helper to get beginning cash
  function getBeginningCash(periodIdx: number, periods: string[], cashCats: string[]): number | null {
    if (periodIdx === 0) return null;
    const prevPeriod = periods[periodIdx - 1] ?? '';
    if (!prevPeriod) return null;
    return getCashBalance(cashCats, prevPeriod);
  }
  // Helper to get ending cash
  function getEndingCash(period: string, cashCats: string[]): number {
    return getCashBalance(cashCats, period);
  }
  // Helper to calculate net income
  function calcNetIncome(period: string): number {
    const periodAccounts = accounts.filter(a => a.month === period);
    const rev = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value);
    const contraRev = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value);
    const exp = activeTemplate.categories.filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 'income-tax-expense', 'other-expense'].includes(cat.type)).map(cat => cat.value);
    const revenue = periodAccounts.filter(a => rev.includes(a.category as string)).reduce((t, a) => t + (a.credit - a.debit), 0);
    const contraRevenue = periodAccounts.filter(a => contraRev.includes(a.category as string)).reduce((t, a) => t + (a.debit - a.credit), 0);
    const expenses = periodAccounts.filter(a => exp.includes(a.category as string)).reduce((t, a) => t + (a.debit - a.credit), 0);
    return revenue - contraRevenue - expenses;
  }
  // Category lists
  type CategoryOption = typeof activeTemplate.categories[0];
  const getCategoriesByFlag = (flag: keyof CategoryOption): string[] => activeTemplate.categories.filter(cat => cat[flag]).map(cat => cat.value);
  const getOrderedNonCashCategories = (): CategoryOption[] => {
    const ordered: CategoryOption[] = [];
    const dep = activeTemplate.categories.find(cat => cat.label.toLowerCase().includes('depreciation') && cat.isNonCash);
    if (dep) ordered.push(dep);
    const amort = activeTemplate.categories.find(cat => cat.label.toLowerCase().includes('amortization') && cat.isNonCash);
    if (amort) ordered.push(amort);
    const sbc = activeTemplate.categories.find(cat => cat.label.toLowerCase().includes('stock') && cat.label.toLowerCase().includes('comp') && cat.isNonCash);
    if (sbc) ordered.push(sbc);
    const others = activeTemplate.categories.filter(cat => cat.isNonCash && !ordered.some(o => o.value === cat.value));
    for (const cat of others) ordered.push(cat);
    return ordered;
  };
  const getNWCRoleCategories = (role: string): string[] => activeTemplate.categories.filter(cat => cat.nwcRole === role).map(cat => cat.value);
  const getOtherCurrent = (type: 'asset' | 'liability'): string[] => {
    if (type === 'asset') {
      return activeTemplate.categories.filter(cat => cat.type === 'current-asset' && cat.nwcRole !== 'cash' && cat.nwcRole !== 'accounts-receivable' && cat.nwcRole !== 'inventory' && cat.nwcRole !== 'debt-like' && !cat.isNonCash).map(cat => cat.value);
    } else {
      return activeTemplate.categories.filter(cat => cat.type === 'current-liability' && cat.nwcRole !== 'accounts-payable' && cat.nwcRole !== 'short-term-debt' && cat.nwcRole !== 'debt-like' && !cat.isNonCash).map(cat => cat.value);
    }
  };
  const getCashCategories = (): string[] => activeTemplate.categories.filter(cat => cat.nwcRole === 'cash').map(cat => cat.value);

  // Periods
  const periods = getAllMonths(accounts);
  const nPeriods = periods.length;

  // Category lists
  const nonCashRows = getOrderedNonCashCategories();
  const ppeCategories = getCategoriesByFlag('isPpe');
  const debtCategories = getCategoriesByFlag('isDebt');
  const equityCategories: string[] = getCategoriesByFlag('isEquityCapital');
  const wcReceivables = getNWCRoleCategories('accounts-receivable');
  const wcInventory = getNWCRoleCategories('inventory');
  const wcPayables = getNWCRoleCategories('accounts-payable');
  const otherCurrentAssets = getOtherCurrent('asset');
  const otherCurrentLiabilities = getOtherCurrent('liability');
  const cashCats = getCashCategories();

  // Section/line item definitions (ordered)
  const cfSections = [
    // Operating Activities
    (() => {
      const section = {
        name: 'Operating Activities',
        key: 'net_cash_from_operating_activities',
        line_items: [
          { name: 'Net Income', key: 'net_income', values: periods.map(p => calcNetIncome(p)) },
          ...nonCashRows.filter(catObj => typeof catObj.value === 'string').map(catObj => ({ name: `Add: ${catObj.label}`, key: `add_${catObj.value}`, values: periods.map(p => sumCategory(catObj.value, p)) })),
          { name: 'Change in Trade Receivables', key: 'change_in_trade_receivables', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? getChange(wcReceivables.filter((c): c is string => typeof c === 'string'), p, prev, true) ?? 0 : 0; }) },
          { name: 'Change in Inventory', key: 'change_in_inventory', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? getChange(wcInventory.filter((c): c is string => typeof c === 'string'), p, prev, true) ?? 0 : 0; }) },
          { name: 'Change in Other Current Assets (excl. non-cash)', key: 'change_in_other_current_assets', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? getChange(otherCurrentAssets.filter((c): c is string => typeof c === 'string'), p, prev, true) ?? 0 : 0; }) },
          { name: 'Change in Trade Payables', key: 'change_in_trade_payables', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? getChange(wcPayables.filter((c): c is string => typeof c === 'string'), p, prev, false, true) ?? 0 : 0; }) },
          { name: 'Change in Other Current Liabilities (excl. non-cash)', key: 'change_in_other_current_liabilities', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? getChange(otherCurrentLiabilities.filter((c): c is string => typeof c === 'string'), p, prev, false, true) ?? 0 : 0; }) },
        ]
      };
      // Add section total
      section.line_items.push({
        name: 'Net Cash from Operating Activities',
        key: 'section_total',
        values: periods.map((_, i) => section.line_items.reduce((sum, item: any) => sum + (item.values[i] || 0), 0))
      });
      return section;
    })(),
    // Investing Activities
    (() => {
      const section = {
        name: 'Investing Activities',
        key: 'net_cash_from_investing_activities',
        line_items: [
          { name: 'Capital Expenditures (PPE)', key: 'capex_ppe', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? -calcSumChange(p, prev, ppeCategories.filter((c): c is string => typeof c === 'string'), true) : 0; }) },
          { name: 'Asset Sales/Dispositions (Net)', key: 'asset_sales_dispositions', values: periods.map(() => 0) },
          { name: 'Other Investing Activities', key: 'other_investing_activities', values: periods.map(() => 0) },
        ]
      };
      // Add section total
      section.line_items.push({
        name: 'Net Cash from Investing Activities',
        key: 'section_total',
        values: periods.map((_, i) => section.line_items.reduce((sum, item: any) => sum + (item.values[i] || 0), 0))
      });
      return section;
    })(),
    // Financing Activities
    (() => {
      const section = {
        name: 'Financing Activities',
        key: 'net_cash_from_financing_activities',
        line_items: [
          { name: 'Debt Raise/Paydown (Net)', key: 'debt_raise_paydown', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? calcSumChange(p, prev, debtCategories.filter((c): c is string => typeof c === 'string')) : 0; }) },
          { name: 'Equity Raise/Repurchase (Net)', key: 'equity_raise_repurchase', values: periods.map((p, i) => { const prev = getPrevPeriod(i, periods); return prev ? calcSumChange(p, prev, equityCategories.filter((c): c is string => typeof c === 'string')) : 0; }) },
          { name: 'Other Financing Activities', key: 'other_financing_activities', values: periods.map(() => 0) },
        ]
      };
      // Add section total
      section.line_items.push({
        name: 'Net Cash from Financing Activities',
        key: 'section_total',
        values: periods.map((_, i) => section.line_items.reduce((sum, item: any) => sum + (item.values[i] || 0), 0))
      });
      return section;
    })(),
    // Cash Roll-Forward (unchanged)
    {
      name: 'Cash Roll-Forward',
      key: 'cash_roll_forward',
      line_items: [
        { name: 'Beginning Cash', key: 'beginning_cash', values: periods.map((p, i) => getBeginningCash(i, periods, cashCats) ?? 0) },
        { name: 'Net Change in Cash', key: 'net_change_in_cash', values: periods.map((p, i) => {
          if (i === 0) return 0;
          const prev = getPrevPeriod(i, periods);
          if (!prev) return 0;
          // Calculate operating cash flow
          let operatingCashFlow = calcNetIncome(p);
          for (const catObj of nonCashRows) operatingCashFlow += sumCategory(catObj.value, p);
          operatingCashFlow += (getChange(wcReceivables.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcInventory.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(otherCurrentAssets.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcPayables.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          operatingCashFlow += (getChange(otherCurrentLiabilities.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          // Investing
          const investingCashFlow = prev ? -calcSumChange(p, prev, ppeCategories.filter((c): c is string => typeof c === 'string'), true) : 0;
          // Financing
          const financingCashFlow = calcSumChange(p, prev, debtCategories.filter((c): c is string => typeof c === 'string')) + calcSumChange(p, prev, equityCategories.filter((c): c is string => typeof c === 'string'));
          return operatingCashFlow + investingCashFlow + financingCashFlow;
        }) },
        { name: 'Ending Cash (Roll-Forward)', key: 'ending_cash_rollforward', values: periods.map((p, i) => {
          const beginningCash = getBeginningCash(i, periods, cashCats);
          if (beginningCash === null) return 0;
          const prev = getPrevPeriod(i, periods);
          if (!prev) return 0;
          let operatingCashFlow = calcNetIncome(p);
          for (const catObj of nonCashRows) operatingCashFlow += sumCategory(catObj.value, p);
          operatingCashFlow += (getChange(wcReceivables.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcInventory.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(otherCurrentAssets.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcPayables.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          operatingCashFlow += (getChange(otherCurrentLiabilities.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          const investingCashFlow = prev ? -calcSumChange(p, prev, ppeCategories.filter((c): c is string => typeof c === 'string'), true) : 0;
          const financingCashFlow = calcSumChange(p, prev, debtCategories.filter((c): c is string => typeof c === 'string')) + calcSumChange(p, prev, equityCategories.filter((c): c is string => typeof c === 'string'));
          const netChange = operatingCashFlow + investingCashFlow + financingCashFlow;
          return beginningCash + netChange;
        }) },
        { name: 'Cash on Balance Sheet', key: 'cash_on_balance_sheet', values: periods.map(p => getEndingCash(p, cashCats)) },
        { name: 'Check (Ending Cash - Balance Sheet)', key: 'check_ending_cash_vs_balance_sheet', values: periods.map((p, i) => {
          const balanceSheetCash = getEndingCash(p, cashCats);
          const beginningCash = getBeginningCash(i, periods, cashCats);
          if (beginningCash === null) return 0;
          const prev = getPrevPeriod(i, periods);
          if (!prev) return 0;
          let operatingCashFlow = calcNetIncome(p);
          for (const catObj of nonCashRows) operatingCashFlow += sumCategory(catObj.value, p);
          operatingCashFlow += (getChange(wcReceivables.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcInventory.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(otherCurrentAssets.filter((c): c is string => typeof c === 'string'), p, prev, true, false) || 0);
          operatingCashFlow += (getChange(wcPayables.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          operatingCashFlow += (getChange(otherCurrentLiabilities.filter((c): c is string => typeof c === 'string'), p, prev, false, true) || 0);
          const investingCashFlow = prev ? -calcSumChange(p, prev, ppeCategories.filter((c): c is string => typeof c === 'string'), true) : 0;
          const financingCashFlow = calcSumChange(p, prev, debtCategories.filter((c): c is string => typeof c === 'string')) + calcSumChange(p, prev, equityCategories.filter((c): c is string => typeof c === 'string'));
          const netChange = operatingCashFlow + investingCashFlow + financingCashFlow;
          const rollForwardEndingCash = beginningCash + netChange;
          return rollForwardEndingCash - balanceSheetCash;
        }) },
      ]
    }
  ];

  const cfDetailData = {
    periods,
    sections: cfSections
  };
  return cfDetailData;
}

// Build Net Working Capital Detail Report (mirrors old UI/component logic)
export function buildNWCDetailReport(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  months: string[],
  netRevenueTimeSeries?: Record<string, number>,
  viewType: 'monthly' | 'quarterly' | 'yearly' = 'monthly',
  periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'
) {
  // Helper functions and logic copied/adapted from UnifiedDetailedNetWorkingCapital.tsx
  const nwcSections = [
    { id: 'current-assets', title: 'Current Assets', types: ['current-asset', 'contra-current-asset'] },
    { id: 'current-liabilities', title: 'Current Liabilities', types: ['current-liability', 'contra-current-liability'] }
  ];
  const getCategoriesByType = (types: string[]) =>
    activeTemplate.categories.filter(cat => types.includes(cat.type)).map(cat => cat.value).filter((v): v is string => typeof v === 'string' && !!v);
  const getCategoryDisplayName = (categoryValue: string) =>
    activeTemplate.categories.find(cat => cat.value === categoryValue)?.label || categoryValue;
  const getCategoryType = (category: string) =>
    activeTemplate.categories.find(cat => cat.value === category)?.type || 'n/a';
  const getCategoryAccountsByMonth = (category: string, month: string) =>
    accounts.filter(account => account.category === category && account.month === month);
  const calculateMonthlyTotal = (category: string, month: string) => {
    const monthlyAccounts = getCategoryAccountsByMonth(category, month);
    const categoryType = getCategoryType(category);
    const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
    return monthlyAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset)
        ? account.debit - account.credit
        : account.credit - account.debit);
    }, 0);
  };
  const calculateSectionTotal = (types: string[], month: string) => {
    const categories = getCategoriesByType(types);
    return categories.reduce((total, category) => total + calculateMonthlyTotal(category, month), 0);
  };
  // Definitional adjustment helpers
  const getDefinitionalAdjustmentCategories = () =>
    activeTemplate.categories.filter(cat => cat.nwcRole === 'cash' || cat.nwcRole === 'short-term-debt' || cat.nwcRole === 'debt-like').map(cat => cat.value);
  const getNWCRole = (category: string) =>
    activeTemplate.categories.find(cat => cat.value === category)?.nwcRole || null;
  const getTradeWorkingCapitalCategories = () =>
    activeTemplate.categories.filter(cat => cat.nwcRole === 'inventory' || cat.nwcRole === 'accounts-receivable' || cat.nwcRole === 'accounts-payable').map(cat => cat.value);
  // Metrics helpers
  const getMonthsInPeriod = (endMonth: string, viewType: string, allMonths: string[]) => {
    if (viewType === 'monthly') return [endMonth];
    const [endMonthStr, yearStr] = endMonth.split('-');
    const year = parseInt(yearStr);
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(endMonthStr);
    let startMonthIndex;
    if (viewType === 'quarterly') {
      startMonthIndex = monthIndex - (monthIndex % 3);
    } else { // yearly
      startMonthIndex = 0;
    }
    return allMonths.filter(month => {
      const [monthStr, monthYearStr] = month.split('-');
      const monthYear = parseInt(monthYearStr);
      const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      if (monthYear !== year) return false;
      return monthIdx >= startMonthIndex && monthIdx <= monthIndex;
    });
  };
  // Period-aware days calculation based on the period type
  const getPeriodDaysForPeriod = (periodStr: string) => {
    if (periodType === 'monthly') {
      // For monthly period type, use individual month days
      return getPeriodDays(periodStr, 'monthly');
    } else if (periodType === 'quarterly') {
      // For quarterly period type, determine the quarter from the month and calculate quarter days
      const [monthStr, yearStr] = periodStr.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      const quarterNum = Math.floor(monthIndex / 3) + 1;
      const quarterStr = `Q${quarterNum}'${yearStr}`;
      return getPeriodDays(quarterStr, 'quarterly');
    } else { // annual
      // For annual period type, use the full year
      const [, yearStr] = periodStr.split('-');
      const fullYear = (2000 + parseInt(yearStr)).toString();
      return getPeriodDays(fullYear, 'annual');
    }
  };
  // Helper to get account display order for a category
  const getAccountDisplayOrder = (categoryValue: string) => {
    const categoryAccounts = accounts.filter((acc: any) => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map((acc: any) => acc.accountNumber))]
      .filter((acc: any): acc is string => typeof acc === 'string' && !!acc);
    return uniqueAccountNumbers.sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  };
  // Calculate Net Revenue
  const calculateNetRevenue = (month: string) => {
    // Get all revenue categories from the template
    const revenueCategories = activeTemplate 
      ? activeTemplate.categories
          .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
          .map(cat => cat.value)
      : ['sales-revenue', 'sales-returns-and-allowances', 'sales-discounts'];
    // Calculate total for all revenue categories
    return revenueCategories.reduce((total, category) => {
      const monthlyTotal = calculateMonthlyTotal(category, month);
      return total + monthlyTotal;
    }, 0);
  };
  // Main build
  const sortedMonths = [...months].sort(compareMonthYear);
  const line_items: any[] = [];
  // Sections
  nwcSections.forEach(section => {
    line_items.push({ type: 'section_header', name: section.title, section_id: section.id, level: 1 });
    const categories = getCategoriesByType(section.types);
    categories.forEach(categoryValue => {
      const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
      if (!category) return;
      // Only add if there are accounts in any month
      const hasAccounts = sortedMonths.some(month => getCategoryAccountsByMonth(categoryValue, month).length > 0);
      if (!hasAccounts) return;
      line_items.push({ type: 'category_header', name: getCategoryDisplayName(categoryValue), category_value: categoryValue, nwc_role: category?.nwcRole || undefined, values: sortedMonths.map(month => calculateMonthlyTotal(categoryValue, month)), level: 2 });
      // Get accounts in order
      const orderedAccountNumbers = getAccountDisplayOrder(categoryValue);
      // Process accounts
      orderedAccountNumbers.forEach((accountNumber: string) => {
        const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
        // Build monthly values array in sorted order
        const monthlyValues = sortedMonths.map(month => {
          const monthlyAccount = accounts.find(a => a.accountNumber === accountNumber && a.category === categoryValue && a.month === month);
          if (!monthlyAccount) return 0;
          const categoryType = getCategoryType(categoryValue);
          const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
          const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
          return (isAsset || isContraAsset)
            ? monthlyAccount.debit - monthlyAccount.credit
            : monthlyAccount.credit - monthlyAccount.debit;
        });
        // Only add accounts that have some non-zero data
        if (monthlyValues.some(val => val !== 0)) {
          line_items.push({
            type: 'account',
            name: accountName,
            account_number: accountNumber,
            category: categoryValue,
            values: monthlyValues,
            level: 3
          });
        }
      });
      // Category total
      const categoryTotalValues = sortedMonths.map(month => calculateMonthlyTotal(categoryValue, month));
      if (categoryTotalValues.some(val => val !== 0)) {
        line_items.push({ type: 'category_total', name: `Total ${getCategoryDisplayName(categoryValue)}`, category: categoryValue, values: categoryTotalValues, level: 2 });
      }
    });
    // Section total
    const sectionTotalValues = sortedMonths.map(month => calculateSectionTotal(section.types, month));
    if (sectionTotalValues.some(val => val !== 0)) {
      line_items.push({ type: 'section_total', name: `Total ${section.title}`, section_id: section.id, values: sectionTotalValues, level: 1 });
    }
  });
  // Working Capital
  const calculateNetWorkingCapital = (month: string) => {
    const totalCurrentAssets = calculateSectionTotal(['current-asset', 'contra-current-asset'], month);
    const totalCurrentLiabilities = calculateSectionTotal(['current-liability', 'contra-current-liability'], month);
    return totalCurrentAssets - totalCurrentLiabilities;
  };
  const netWorkingCapitalValues = sortedMonths.map(month => calculateNetWorkingCapital(month));
  line_items.push({ type: 'calculated_total', name: 'Working Capital', values: netWorkingCapitalValues, level: 0 });
  // Definitional Adjustments
  const calculateDefinitionalAdjustments = (month: string) => {
    const adjustmentCategories = getDefinitionalAdjustmentCategories();
    return adjustmentCategories.reduce((total, category) => {
      const role = getNWCRole(category);
      if (role === 'cash') {
        return total - calculateMonthlyTotal(category, month);
      }
      if (role === 'short-term-debt' || role === 'debt-like') {
        return total + calculateMonthlyTotal(category, month);
      }
      return total;
    }, 0);
  };
  // Cash row
  const cashCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'cash').map(cat => cat.value);
  const cashValues = sortedMonths.map(month => -cashCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0));
  line_items.push({ type: 'adjustment', name: 'Less: Cash', nwc_role: 'cash', values: cashValues, level: 0 });
  // Short-term Debt row
  const stdCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'short-term-debt').map(cat => cat.value);
  const stdValues = sortedMonths.map(month => stdCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0));
  line_items.push({ type: 'adjustment', name: 'Plus: Short-term Debt', nwc_role: 'short-term-debt', values: stdValues, level: 0 });
  // Total Definitional Adjustments
  const definitionalAdjustmentValues = sortedMonths.map(month => calculateDefinitionalAdjustments(month));
  line_items.push({ type: 'calculated_total', name: 'Total Definitional Adjustments', values: definitionalAdjustmentValues, level: 0 });
  // Net Working Capital After Definitional Adjustments
  const adjustedWorkingCapitalValues = sortedMonths.map((month, idx) => netWorkingCapitalValues[idx] + definitionalAdjustmentValues[idx]);
  line_items.push({ type: 'calculated_total', name: 'Net Working Capital After Definitional Adjustments', values: adjustedWorkingCapitalValues, level: 0 });
  // Net Working Capital % of Net Revenue
  const workingCapitalPercentValues = sortedMonths.map(month => {
    const workingCapital = adjustedWorkingCapitalValues[sortedMonths.indexOf(month)];
    if (viewType === 'monthly' || !netRevenueTimeSeries) {
      // Use local calculation for monthly or if no external net revenue provided
      const netRevenue = calculateNetRevenue(month);
      if (netRevenue === 0) return 0;
      const periodDays = getPeriodDaysForPeriod(month);
      return (workingCapital / netRevenue) * periodDays / 365 * 100;
    } else {
      // For quarterly/yearly, aggregate net revenue from netRevenueTimeSeries
      const monthsInPeriod = getMonthsInPeriod(month, viewType, sortedMonths);
      const sumNetRevenue = monthsInPeriod.reduce((sum, m) => sum + (netRevenueTimeSeries[m] || 0), 0);
      const avgNWC = monthsInPeriod.reduce((sum, m) => sum + (adjustedWorkingCapitalValues[sortedMonths.indexOf(m)] || 0), 0) / monthsInPeriod.length;
      const totalDays = monthsInPeriod.reduce((sum, m) => sum + getPeriodDaysForPeriod(m), 0);
      if (sumNetRevenue === 0) return 0;
      return (avgNWC / sumNetRevenue) * totalDays / 365 * 100;
    }
  });
  line_items.push({ type: 'calculated_percentage', name: 'Net Working Capital % of Net Revenue', values: workingCapitalPercentValues, level: 0 });
  // Metrics section
  line_items.push({ type: 'section_header', name: 'Working Capital Metrics', section_id: 'working-capital-metrics', level: 1 });
  // DIO
  const inventoryCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'inventory').map(cat => cat.value);
  const cogsCategories = activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value);
  const calculateDIO = (month: string) => {
    const inventory = inventoryCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
    const cogs = cogsCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
    const periodDays = getPeriodDaysForPeriod(month);
    
    // Debug DIO calculation
    console.log(`[DIO DEBUG] Period: ${month}, Inventory: ${inventory}, COGS: ${cogs}, Days: ${periodDays}, DIO: ${cogs !== 0 ? -(inventory / cogs) * periodDays : 0}`);
    
    return cogs !== 0 ? -(inventory / cogs) * periodDays : 0;
  };
  const dioValues = sortedMonths.map(month => calculateDIO(month));
  line_items.push({ type: 'calculated_total', name: 'Days Inventory Outstanding (DIO)', values: dioValues, level: 2, formula: 'Inventory / COGS * days' });
  // DSO
  const arCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'accounts-receivable').map(cat => cat.value);
  const calculateDSO = (month: string) => {
    const ar = arCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
    const netRevenue = calculateNetRevenue(month);
    const periodDays = getPeriodDaysForPeriod(month);
    
    // Debug DSO calculation
    console.log(`[DSO DEBUG] Period: ${month}, AR: ${ar}, NetRevenue: ${netRevenue}, Days: ${periodDays}, DSO: ${netRevenue !== 0 ? (ar / netRevenue) * periodDays : 0}`);
    
    return netRevenue !== 0 ? (ar / netRevenue) * periodDays : 0;
  };
  const dsoValues = sortedMonths.map(month => calculateDSO(month));
  line_items.push({ type: 'calculated_total', name: 'Days Sales Outstanding (DSO)', values: dsoValues, level: 2, formula: 'AR / Net Revenue * days' });
  // DPO
  const apCategories = activeTemplate.categories.filter(cat => cat.nwcRole === 'accounts-payable').map(cat => cat.value);
  const calculateDPO = (month: string) => {
    const ap = apCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
    const cogs = cogsCategories.reduce((sum, cat) => sum + calculateMonthlyTotal(cat, month), 0);
    const periodDays = getPeriodDaysForPeriod(month);
    
    // Debug DPO calculation
    console.log(`[DPO DEBUG] Period: ${month}, AP: ${ap}, COGS: ${cogs}, Days: ${periodDays}, DPO: ${cogs !== 0 ? -(ap / cogs) * periodDays : 0}`);
    
    return cogs !== 0 ? -(ap / cogs) * periodDays : 0;
  };
  const dpoValues = sortedMonths.map(month => calculateDPO(month));
  line_items.push({ type: 'calculated_total', name: 'Days Payable Outstanding (DPO)', values: dpoValues, level: 2, formula: 'AP / COGS * days' });
  // Cash Conversion Cycle
  const cccValues = sortedMonths.map((month, idx) => dioValues[idx] + dsoValues[idx] - dpoValues[idx]);
  line_items.push({ type: 'calculated_total', name: 'Cash Conversion Cycle', values: cccValues, level: 1, formula: 'DIO + DSO - DPO', is_final: true });
  return {
    periods: sortedMonths,
    line_items
  };
}

/**
 * buildFinancialRatiosReport
 * Calculates all financial ratios for each month (period) and returns them in a structure suitable for storage and frontend use.
 * The frontend should aggregate these monthly values for quarterly/yearly views as needed.
 */
export function buildFinancialRatiosReport(
  accounts: AccountEntry[],
  activeTemplate: CategoryTemplate,
  allReports: Record<string, any>,
  periods: string[],
  viewType: 'monthly' | 'quarterly' | 'yearly' = 'monthly',
  periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'
) {
  // Ratio definitions (copied from frontend)
  const ratioDefinitions = [
    // Liquidity
    {
      name: 'Current Ratio',
      formula: 'Total Current Assets ÷ Total Current Liabilities',
      description: 'Measures the company\'s ability to pay short-term obligations.',
      account_mappings: ['Total Current Assets', 'Total Current Liabilities'],
      category: 'liquidity',
      format: 'decimal',
      calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : m.currentAssets / m.currentLiabilities
    },
    {
      name: 'Quick Ratio (Acid-Test)',
      formula: '(Cash + Short-Term Investments + Trade Accounts Receivable) ÷ Total Current Liabilities',
      description: 'Measures liquidity excluding inventory.',
      account_mappings: ['Cash', 'Short-Term Investments', 'Trade Accounts Receivable', 'Total Current Liabilities'],
      category: 'liquidity',
      format: 'decimal',
      calc: (m: any) => {
        if (m.currentLiabilities === 0 || m.currentLiabilities === null) return null;
        const quickAssets = (m.cash || 0) + (m.shortTermInvestments || 0) + (m.accountsReceivable || 0);
        return quickAssets / m.currentLiabilities;
      }
    },
    {
      name: 'Cash Ratio',
      formula: 'Cash ÷ Total Current Liabilities',
      description: 'Measures the ability to pay short-term debts with cash.',
      account_mappings: ['Cash', 'Total Current Liabilities'],
      category: 'liquidity',
      format: 'decimal',
      calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : (m.cash || 0) / m.currentLiabilities
    },
    {
      name: 'Operating Cash Flow Ratio',
      formula: 'Operating Cash Flow ÷ Total Current Liabilities',
      description: 'Measures the ability to pay current liabilities with operating cash flow.',
      account_mappings: ['Operating Cash Flow', 'Total Current Liabilities'],
      category: 'liquidity',
      format: 'decimal',
      calc: (m: any) => (m.currentLiabilities === 0 || m.currentLiabilities === null || !m.operatingCashFlow) ? null : m.operatingCashFlow / m.currentLiabilities
    },
    // Profitability
    {
      name: 'Gross Profit Margin',
      formula: 'Gross Profit ÷ Net Revenue',
      description: 'Measures gross profitability.',
      account_mappings: ['Gross Profit', 'Net Revenue'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.grossProfit || 0) / m.netRevenue
    },
    {
      name: 'Operating Margin',
      formula: 'Operating Income ÷ Net Revenue',
      description: 'Measures operating profitability.',
      account_mappings: ['Operating Income', 'Net Revenue'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.operatingIncome || 0) / m.netRevenue
    },
    {
      name: 'EBITDA Margin',
      formula: 'EBITDA ÷ Net Revenue',
      description: 'Measures cash profitability before interest, taxes, depreciation, and amortization.',
      account_mappings: ['EBITDA', 'Net Revenue'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.ebitda || 0) / m.netRevenue
    },
    {
      name: 'Net Profit Margin',
      formula: 'Net Income ÷ Net Revenue',
      description: 'Measures overall profitability.',
      account_mappings: ['Net Income', 'Net Revenue'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.netIncome || 0) / m.netRevenue
    },
    {
      name: 'Return on Assets (ROA)',
      formula: 'Net Income ÷ Total Assets',
      description: 'Measures profitability relative to total assets.',
      account_mappings: ['Net Income', 'Total Assets'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.totalAssets === 0 || m.totalAssets === null ? null : (m.netIncome || 0) / m.totalAssets
    },
    {
      name: 'Return on Equity (ROE)',
      formula: 'Net Income ÷ Total Equity',
      description: 'Measures profitability relative to shareholders\' equity.',
      account_mappings: ['Net Income', 'Total Equity'],
      category: 'profitability',
      format: 'percentage',
      calc: (m: any) => m.totalEquity === 0 || m.totalEquity === null ? null : (m.netIncome || 0) / m.totalEquity
    },
    // Leverage
    {
      name: 'Debt-to-Capital',
      formula: '(Short-term Debt + Long-term Debt) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
      description: 'Measures debt as a percentage of total capital.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
      category: 'leverage',
      format: 'percentage',
      calc: (m: any) => {
        const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        const totalCapital = totalDebt + (m.totalEquity || 0);
        return totalCapital === 0 ? null : totalDebt / totalCapital;
      }
    },
    {
      name: 'Net Debt-to-Capital',
      formula: '(Short-term Debt + Long-term Debt - Cash) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
      description: 'Measures net debt as a percentage of total capital.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
      category: 'leverage',
      format: 'percentage',
      calc: (m: any) => {
        const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        const netDebt = grossDebt - (m.cash || 0);
        const totalCapital = grossDebt + (m.totalEquity || 0);
        return totalCapital === 0 ? null : netDebt / totalCapital;
      }
    },
    {
      name: 'Debt-to-Equity',
      formula: '(Short-term Debt + Long-term Debt) ÷ Total Equity',
      description: 'Measures debt relative to equity.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
      category: 'leverage',
      format: 'percentage',
      calc: (m: any) => {
        if (m.totalEquity === 0 || m.totalEquity === null) return null;
        const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        return totalDebt / m.totalEquity;
      }
    },
    {
      name: 'Net Debt-to-Equity',
      formula: '(Short-term Debt + Long-term Debt - Cash) ÷ Total Equity',
      description: 'Measures net debt relative to equity.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
      category: 'leverage',
      format: 'percentage',
      calc: (m: any) => {
        if (m.totalEquity === 0 || m.totalEquity === null) return null;
        const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        const netDebt = grossDebt - (m.cash || 0);
        return netDebt / m.totalEquity;
      }
    },
    {
      name: 'Leverage Ratio',
      formula: '(Short-term Debt + Long-term Debt) ÷ EBITDA',
      description: 'Measures debt relative to earnings.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'EBITDA'],
      category: 'leverage',
      format: 'multiple',
      calc: (m: any) => {
        if (m.ebitda === 0 || m.ebitda === null) return null;
        const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        return totalDebt / m.ebitda;
      }
    },
    {
      name: 'Net Leverage Ratio',
      formula: '(Short-term Debt + Long-term Debt - Cash) ÷ EBITDA',
      description: 'Measures net debt relative to earnings.',
      account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'EBITDA'],
      category: 'leverage',
      format: 'multiple',
      calc: (m: any) => {
        if (m.ebitda === 0 || m.ebitda === null) return null;
        const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
        const netDebt = grossDebt - (m.cash || 0);
        return netDebt / m.ebitda;
      }
    },
    {
      name: 'Interest Coverage Ratio',
      formula: 'EBITDA ÷ Interest Expense',
      description: 'Measures ability to pay interest expenses.',
      account_mappings: ['EBITDA', 'Interest Expense'],
      category: 'leverage',
      format: 'multiple',
      calc: (m: any) => (m.interestExpense === 0 || m.interestExpense === null || m.ebitda === null) ? null : m.ebitda / m.interestExpense
    }
  ];
  const sections = [
    { id: 'liquidity', name: 'Liquidity Ratios', ratios: ratioDefinitions.filter(r => r.category === 'liquidity') },
    { id: 'profitability', name: 'Profitability Ratios', ratios: ratioDefinitions.filter(r => r.category === 'profitability') },
    { id: 'leverage', name: 'Leverage Ratios', ratios: ratioDefinitions.filter(r => r.category === 'leverage') }
  ];
  const line_items: any[] = [];
  sections.forEach(section => {
    // Add section header
    line_items.push({
      name: section.name,
      type: 'section_header',
      level: 1,
      values: periods.map(() => null),
      category: section.id
    });
    // Add ratios
    section.ratios.forEach(ratio => {
      const values = periods.map(period => {
        const metrics = getMetrics(period, allReports, viewType, periodType);
        const result = ratio.calc(metrics);
        return result;
      });
      line_items.push({
        name: ratio.name,
        type: 'ratio',
        level: 1,
        formula: ratio.formula,
        description: ratio.description,
        account_mappings: ratio.account_mappings,
        values,
        category: section.id,
        format: ratio.format
      });
    });
  });
  return { periods, line_items, sections };
}

// --- Add helpers for backend ratio calculation (mimic oldFinancialRatios.tsx) ---
function getVal(lineItem: string, period: string, allReports: Record<string, any>): number | null {
  for (const key of ['pnl_detail', 'bs_detail', 'cf_detail']) {
    const report = allReports[key];
    if (!report) continue;
    // Array-based format: { periods: [...], line_items: [{ name, values }] }
    if (report.periods && report.line_items) {
      const periodIdx = report.periods.indexOf(period);
      if (periodIdx === -1) continue;
      const item = report.line_items.find((li: any) => li.name === lineItem);
      if (item?.values && item.values[periodIdx] !== undefined) {
        return item.values[periodIdx];
      }
    }
    // Legacy dict-based format fallback: { data: { [lineItem]: { [period]: value } } }
    if (report.data?.[lineItem]?.[period] !== undefined) {
      return report.data[lineItem][period];
    }
  }
  return null;
}

function getReportForLineItem(lineItem: string, allReports: Record<string, any>): any {
  for (const key of ['pnl_detail', 'bs_detail', 'cf_detail']) {
    const report = allReports[key];
    if (!report) continue;
    // Array-based format
    if (report.line_items?.some((li: any) => li.name === lineItem)) return report;
    // Legacy dict-based format
    if (report.data?.[lineItem]) return report;
  }
  return null;
}

function getAnnualized(lineItem: string, period: string, viewType: 'monthly' | 'quarterly' | 'yearly', periodType: 'monthly' | 'quarterly' | 'annual', allReports: Record<string, any>): number | null {
  // Check if this is a P&L item
  const pnlReport = allReports?.pnl_detail;
  let isPLItem = false;
  if (pnlReport?.line_items) {
    isPLItem = pnlReport.line_items.some((li: any) => li.name === lineItem);
  } else if (pnlReport?.data) {
    isPLItem = !!pnlReport.data[lineItem];
  }

  const report = getReportForLineItem(lineItem, allReports);
  if (!report) return null;

  // Get value from the report
  let value: number | null = null;
  if (report.periods && report.line_items) {
    // Array-based format
    const pIdx = report.periods.indexOf(period);
    if (pIdx === -1) return null;
    const item = report.line_items.find((li: any) => li.name === lineItem);
    value = item?.values?.[pIdx] ?? null;
  } else if (report.data?.[lineItem]) {
    // Legacy dict-based format
    value = report.data[lineItem][period] ?? null;
  }

  if (value == null) return null;

  if (isPLItem) {
    // P&L items need annualization based on period type
    let annualizationFactor: number;
    if (periodType === 'annual') {
      annualizationFactor = 1;
    } else if (periodType === 'quarterly') {
      annualizationFactor = viewType === 'quarterly' ? 4 : 1;
    } else {
      annualizationFactor = viewType === 'monthly' ? 12 : 1;
    }
    return value * annualizationFactor;
  } else {
    // Balance sheet or other point-in-time items
    return value;
  }
}

function getMetrics(period: string, allReports: Record<string, any>, viewType: 'monthly' | 'quarterly' | 'yearly', periodType: 'monthly' | 'quarterly' | 'annual') {
  return {
    currentAssets: getVal('Total Current Assets', period, allReports),
    currentLiabilities: getVal('Total Current Liabilities', period, allReports),
    totalAssets: getVal('Total Assets', period, allReports),
    totalLiabilities: getVal('Total Liabilities', period, allReports),
    totalEquity: getVal('Total Equity', period, allReports),
    cash: getVal('Total Cash', period, allReports) || getVal('Cash', period, allReports),
    shortTermInvestments: getVal('Short-Term Investments', period, allReports),
    inventory: getVal('Inventory', period, allReports),
    accountsReceivable: getVal('Total Trade Accounts Receivable', period, allReports),
    accountsPayable: getVal('Trade Accounts Payable', period, allReports),
    shortTermDebt: getVal('Total Short-term Debt', period, allReports),
    longTermDebt: getVal('Total Long-term Debt', period, allReports),
    totalRevenue: getAnnualized('Total Revenue', period, viewType, periodType, allReports),
    netRevenue: getAnnualized('Net Revenue', period, viewType, periodType, allReports),
    grossProfit: getAnnualized('Gross Margin', period, viewType, periodType, allReports),
    operatingIncome: getAnnualized('Operating Income', period, viewType, periodType, allReports),
    netIncome: getAnnualized('Net Income', period, viewType, periodType, allReports),
    ebitda: getAnnualized('EBITDA', period, viewType, periodType, allReports),
    interestExpense: Math.abs(getAnnualized('Interest Expense (Income)', period, viewType, periodType, allReports) || 0),
    operatingCashFlow: getVal('Net Cash from Operating Activities', period, allReports),
  };
} 